// client/src/hooks/useAccountPage.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';
import { useAuth } from '../context/auth/AuthProvider';

const ACCOUNT_PAGE_QUERY_KEY = 'accountPageConfig';

// pageConfig é um path de verdade em Account.pageConfig (server/models/Account.js)
// — o nome antigo "paginaPublica" nunca existiu no schema, então nada salvo
// nele persistia. Usa o endpoint dedicado (accountPageController.js,
// montado em /contas/... — accountsRoutes.js é montado em /api/v1/contas,
// não /accounts).
//
// ⚠️ Duas rotas existem e NÃO são intercambiáveis: /contas/:id/page-config
// exige a permissão 'contas:edit' (grupo "Administração Franquia" — matriz/
// franquia gerenciando a conta de outra pessoa), enquanto /contas/my/page-config
// exige 'configuracoes_conta:edit' (autoatendimento). Um usuário conta_admin
// comum tem a segunda, não a primeira — usar sempre a rota /:id/ (como
// antes) dava 403 pra quem edita a própria conta em /minha-conta/pagina-publica.
// scope==='conta' sempre está editando a própria conta aqui (não existe
// cenário de conta_admin editando a página de outra conta), então usa /my/
// nesse caso e /:id/ só pra matriz/franquia.
const buildPageConfigPath = (scope, accountId) =>
    scope === 'conta' ? '/contas/my/page-config' : `/contas/${accountId}/page-config`;

export const useAccountPageConfig = (accountId) => {
    const { setAlert } = useAlert();
    const { scope } = useAuth();

    return useQuery({
        queryKey: [ACCOUNT_PAGE_QUERY_KEY, accountId],
        queryFn: async () => {
            if (!accountId) return null;
            const { data } = await api.get(buildPageConfigPath(scope, accountId));
            return data.data?.pageConfig || {};
        },
        enabled: !!accountId,
        onError: (err) => {
            console.error("Erro ao buscar configurações da página:", err);
            setAlert(err.response?.data?.error || 'Erro ao carregar configurações', 'danger');
        }
    });
};

export const useMutateAccountPage = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    const { scope } = useAuth();

    const updatePageConfig = useMutation({
        mutationFn: async ({ accountId, ...pageConfigData }) => {
            return api.put(buildPageConfigPath(scope, accountId), pageConfigData);
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [ACCOUNT_PAGE_QUERY_KEY, variables.accountId]
            });
            queryClient.invalidateQueries({
                queryKey: ['accounts', variables.accountId]
            });
            setAlert('Configurações da página salvas com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("Erro ao atualizar página:", err.response?.data);
            setAlert(err.response?.data?.error || 'Erro ao salvar configurações', 'danger');
        }
    });

    const togglePublicPage = useMutation({
        mutationFn: async (accountId) => {
            return api.patch(`${buildPageConfigPath(scope, accountId)}/toggle`);
        },
        onSuccess: (data, accountId) => {
            queryClient.invalidateQueries({
                queryKey: [ACCOUNT_PAGE_QUERY_KEY, accountId]
            });
            queryClient.invalidateQueries({
                queryKey: ['accounts', accountId]
            });

            const enabled = data.data?.data?.pageConfig?.enabled;
            const message = enabled ?
                'Página pública ativada!' : 'Página pública desativada!';
            setAlert(message, 'success');
        },
        onError: (err) => {
            console.error("Erro ao alternar página:", err.response?.data);
            setAlert(err.response?.data?.error || 'Erro ao alterar status da página', 'danger');
        }
    });

    return { updatePageConfig, togglePublicPage };
}